import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';

/**
 * Where a booking stands in its state machine (`03-meetings.md`).
 *
 * ## Five values, and none of them is transient
 *
 * `03-meetings.md` draws the machine as
 * `requested → confirmed_one → confirmed_both → scheduled`, and two of those
 * never rest. The requester **auto-confirms**, so `confirmed_one` is the state a
 * booking is born in rather than one it moves to; and `confirmed_both` either
 * needs a guardian or becomes `scheduled` **inside the same transaction**, which
 * is what stops a booking being briefly ungated or permanently frozen (P24b2's
 * "decide the gate before the transaction and write status and rows together").
 *
 * A status nothing can ever be found in is still a status every consumer has to
 * handle, every switch has to branch on, and every screen has to render. So the
 * resting states are the whole list:
 *
 * - `requested` — the requester has confirmed; the counterparty has not.
 * - `pending_guardian` — both members have; at least one ward's wali has not.
 * - `scheduled` — everything required is confirmed. This is the state a member
 *   is told out loud, and the one a reminder fires against.
 * - `completed` — the time came and staff recorded what happened.
 * - `cancelled` — terminal, reachable from every state above it.
 * - `expired` — terminal; the window came within `meetings.bookingLeadTimeMinutes`
 *   of starting while still `requested` or `pending_guardian`, so nobody can
 *   confirm it any more. Stamped by the expiry sweep, never by a person — a
 *   request nobody answered must not sit "awaiting" for ever, nor count as a
 *   live booking against the pair's limit.
 *
 * `pending_guardian` exists only where `meetings.guardianConfirmationRequired`
 * resolves true for a member whose ward actually has a guardian — a ward with
 * nobody to ask is unaffected, the rule P24b2 established for the chat gate and
 * this inherits verbatim.
 *
 * `expired` is listed LAST: the Postgres enum was widened with a plain
 * `ADD VALUE`, and a freshly created database must sort the column the way a
 * migrated one does.
 */
export const BOOKING_STATUSES = [
  'requested',
  'pending_guardian',
  'scheduled',
  'completed',
  'cancelled',
  'expired',
] as const;

export const BookingStatusSchema = z.enum(BOOKING_STATUSES);

export type BookingStatus = (typeof BOOKING_STATUSES)[number];

/**
 * The states nothing reopens.
 *
 * Declared as its own list rather than derived by negating "live", because
 * **every claim in this domain is written against it**: a confirmation, a
 * cancellation and an outcome all claim on "not already terminal" rather than on
 * `= 'requested'`. P24b2 recorded what the other spelling costs — a claim that
 * named `active` silently excluded a state invented two phases later, and the
 * write was lost with nothing raised.
 */
export const BOOKING_TERMINAL_STATUSES = ['completed', 'cancelled', 'expired'] as const;

export type TerminalBookingStatus = (typeof BOOKING_TERMINAL_STATUSES)[number];

const TERMINAL_SET: ReadonlySet<string> = new Set(BOOKING_TERMINAL_STATUSES);

/**
 * Whether a booking has finished moving.
 *
 * The one place the question is answered, so a sixth status added later is
 * classified here and not in five call sites that each guessed.
 */
export function isTerminalBookingStatus(status: BookingStatus): status is TerminalBookingStatus {
  return TERMINAL_SET.has(status);
}

/**
 * What happened at a meeting that was actually scheduled.
 *
 * **Cancellation is deliberately not here.** It is a *status*: it happens before
 * the meeting, it is caused by a person, and the booking records who and when.
 * An outcome answers a different question — "the time came, what happened" — and
 * only a `scheduled` booking ever reaches it. Collapsing the two would make
 * "cancelled" mean both "called off on Tuesday" and "nobody came on Thursday".
 *
 * `not_held` is the venue's answer, distinct from `no_show`: the room was
 * closed, the supervisor did not arrive, the building flooded. Blaming a member
 * for the partner's failure is a trust defect, not a rounding error.
 *
 * **Who failed to attend is `outcomeNotes`, not a fourth value**, and that is not
 * a free-text shrug (U0). It is genuinely narrative: a member can arrive late,
 * leave early, bring a guardian who was not expected, or attend while the other
 * cancels at the door. An enum here would make staff choose the nearest wrong
 * answer, which is worse than a sentence.
 */
export const BOOKING_OUTCOMES = ['attended', 'no_show', 'not_held'] as const;

export const BookingOutcomeSchema = z.enum(BOOKING_OUTCOMES);

export type BookingOutcome = (typeof BOOKING_OUTCOMES)[number];

/**
 * One booking, as its payload carries it (`03-meetings.md`).
 *
 * ## The slot's window is copied here, and that is a read convenience
 *
 * `startsAt`/`endsAt` are the slot's, resolved at read time — never a second
 * source of truth. They are on the payload because every surface that draws a
 * booking draws its time, and making each one fetch the slot to learn when the
 * meeting is would be a second request for a fact this response already held.
 * A re-timed slot is refused while a booking names it (`venue_slot_in_use`), so
 * the two cannot drift.
 *
 * ## `guardianConfirmationsOutstanding` is a COUNT, and never the guardians
 *
 * A member waiting on the other side's wali needs to know she is waiting; he
 * does not get to learn who her guardians are. The number says "waiting on one
 * more" without disclosing an arrangement that belongs to her account — the same
 * line P24b1 drew when it refused to hand a guardian a photograph he could never
 * exchange for bytes.
 *
 * ## What this does NOT re-check
 *
 * `outcomeNotes` carries no length bound here. A read DTO describes what the
 * server **sends**, so re-validating a write-side bound makes a stored row
 * unreadable the day that bound is lowered (P25a1). The bound lives on the write
 * contract, where the value arrives from a person.
 */
export const BookingSchema = z
  .object({
    id: UuidSchema,
    matchId: UuidSchema,
    venueId: UuidSchema,
    slotId: UuidSchema,
    /** The slot's window, resolved on read. See the note above. */
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    status: BookingStatusSchema,
    /** The member who asked. They are confirmed by the act of asking. */
    requestedByUserId: UuidSchema,
    /** The counterparty, once they have confirmed. `null` while `requested`. */
    confirmedByUserId: UuidSchema.nullable(),
    confirmedAt: z.iso.datetime().nullable(),
    /** When everything required had been confirmed. `null` until `scheduled`. */
    scheduledAt: z.iso.datetime().nullable(),
    /**
     * Who cancelled. `null` **with** a `cancelledAt` means the platform did —
     * a match that ended takes its bookings with it, and no person did that.
     */
    cancelledByUserId: UuidSchema.nullable(),
    cancelledAt: z.iso.datetime().nullable(),
    /** When the sweep gave up on it. `null` unless `expired`. */
    expiredAt: z.iso.datetime().nullable(),
    outcome: BookingOutcomeSchema.nullable(),
    outcomeNotes: z.string().nullable(),
    /**
     * The venue's name in the reader's locale, resolved on read.
     *
     * On the payload because a surface that draws a uuid where a building's name
     * belongs is an unfinished one (U0) — and that is truer of the member's own
     * bookings than of the oversight table, because it is their meeting. `null`
     * means the lookup came back short (the venue was withdrawn, or carries no
     * translation in this locale) and **never** "no venue": `venue_id` is a
     * notNull foreign key.
     *
     * It lives here rather than on `AdminBookingSchema`, where P25d first put it,
     * because that schema is documented as a **superset** of this one: declaring
     * the field twice in two parallel extensions is how the two payloads start
     * describing the same booking differently. Moved down by P25e1, when the
     * member gained a list that draws it.
     */
    venueName: z.string().nullable(),
    /** How many wali confirmations are still outstanding. See the note above. */
    guardianConfirmationsOutstanding: z.number().int(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .strict();

export type Booking = z.infer<typeof BookingSchema>;
